import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "module-rotation-calendar" }
const _hoisted_2 = { class: "flex items-center justify-between mb-6" }
const _hoisted_3 = { class: "text-sm text-secondary-text dark:text-secondary-dark-text mt-1" }
const _hoisted_4 = { class: "flex items-center space-x-2" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "text-lg font-medium text-primary-text dark:text-primary-dark-text min-w-[80px] text-center" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 0,
  class: "flex justify-center items-center py-12"
}
const _hoisted_9 = {
  key: 1,
  class: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
}
const _hoisted_10 = { class: "flex items-center" }
const _hoisted_11 = { class: "text-red-700 dark:text-red-300" }
const _hoisted_12 = {
  key: 2,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
}
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "flex items-center justify-between min-w-0" }
const _hoisted_15 = { class: "font-semibold text-primary-text dark:text-primary-dark-text text-lg truncate" }
const _hoisted_16 = { class: "p-5" }
const _hoisted_17 = {
  key: 0,
  class: "space-y-4"
}
const _hoisted_18 = { class: "flex items-center space-x-3" }
const _hoisted_19 = { class: "flex-1 min-w-0" }
const _hoisted_20 = { class: "font-semibold text-primary-text dark:text-primary-dark-text truncate text-base group-hover:text-primary transition-colors" }
const _hoisted_21 = { class: "bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600" }
const _hoisted_22 = { class: "flex items-center text-sm text-secondary-text dark:text-secondary-dark-text" }
const _hoisted_23 = { class: "font-medium" }
const _hoisted_24 = { class: "w-full" }
const _hoisted_25 = { class: "flex justify-between text-xs text-secondary-text dark:text-secondary-dark-text mb-2" }
const _hoisted_26 = { class: "font-semibold" }
const _hoisted_27 = { class: "w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 shadow-inner" }
const _hoisted_28 = {
  key: 1,
  class: "text-center py-8"
}
const _hoisted_29 = { class: "bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3" }

import { ref, onMounted, computed, watch } from 'vue'
import { ModulesService, type ModuleRotation } from '@/services/modules'
import ModuleDetailsModal from '@/components/modals/ModuleDetailsModal.vue'
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  AlertCircle
} from 'lucide-vue-next'

// Define month interface
interface MonthData {
  number: number
  name: string
  module: ModuleRotation | null
  isCurrentMonth: boolean
}

// Reactive data

export default /*@__PURE__*/_defineComponent({
  __name: 'ModuleRotationCalendar',
  setup(__props) {

const currentYear = ref(new Date().getFullYear())
const rotations = ref<ModuleRotation[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const showModal = ref(false)
const selectedMonth = ref<MonthData | null>(null)

// Month names
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

// Computed properties
const monthsData = computed(() => {
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth() + 1
  const currentYearActual = currentDate.getFullYear()
  
  return monthNames.map((name, index) => {
    const monthNumber = index + 1
    const moduleForMonth = rotations.value.find(rotation => 
      rotation.month === monthNumber && rotation.year === currentYear.value
    )
    
    return {
      number: monthNumber,
      name,
      module: moduleForMonth || null,
      isCurrentMonth: currentYear.value === currentYearActual && monthNumber === currentMonth
    }
  })
})

// Methods
const fetchRotations = async () => {
  loading.value = true
  error.value = null
  
  try {
    rotations.value = await ModulesService.getModuleRotations(currentYear.value)
  } catch (err: any) {
    error.value = err.message || 'Failed to fetch module rotations'
  } finally {
    loading.value = false
  }
}

const changeYear = (delta: number) => {
  currentYear.value += delta
}

const getModuleStatusColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'bg-green-500'
  if (module.is_future) return 'bg-blue-500'
  return 'bg-gray-400'
}

const getModuleStatusTextColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'text-green-600 dark:text-green-400'
  if (module.is_future) return 'text-blue-600 dark:text-blue-400'
  return 'text-gray-500 dark:text-gray-400'
}

const getModuleStatusText = (module: ModuleRotation): string => {
  if (module.is_current) return 'Current Module'
  if (module.is_future) return 'Upcoming Module'
  return 'Past Module'
}

const getProgressBarColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'bg-green-500'
  if (module.is_future) return 'bg-blue-500'
  return 'bg-gray-400'
}

const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  const startDay = start.getDate()
  const endDay = end.getDate()
  const month = start.toLocaleDateString('en-US', { month: 'short' })
  
  return `${month} ${startDay}-${endDay}`
}

const getMonthProgress = (module: ModuleRotation): number => {
  if (module.is_past) return 100
  if (module.is_future) return 0
  
  // For current module, calculate actual progress
  const start = new Date(module.start_date)
  const end = new Date(module.end_date)
  const now = new Date()
  
  const total = end.getTime() - start.getTime()
  const elapsed = now.getTime() - start.getTime()
  
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)))
}

// Modal methods
const openMonthModal = (month: MonthData) => {
  selectedMonth.value = month
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  selectedMonth.value = null
}

// Watch for year changes
watch(currentYear, () => {
  fetchRotations()
})

// Initialize
onMounted(() => {
  fetchRotations()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, [
        _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "text-xl font-semibold text-primary-text dark:text-primary-dark-text" }, " Module Rotation Calendar ", -1)),
        _createElementVNode("p", _hoisted_3, " View training module schedule for " + _toDisplayString(currentYear.value), 1)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (changeYear(-1))),
          class: "p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors",
          disabled: loading.value
        }, [
          _createVNode(_unref(ChevronLeft), { class: "w-4 h-4 text-gray-600 dark:text-gray-300" })
        ], 8, _hoisted_5),
        _createElementVNode("span", _hoisted_6, _toDisplayString(currentYear.value), 1),
        _createElementVNode("button", {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (changeYear(1))),
          class: "p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors",
          disabled: loading.value
        }, [
          _createVNode(_unref(ChevronRight), { class: "w-4 h-4 text-gray-600 dark:text-gray-300" })
        ], 8, _hoisted_7)
      ])
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[3] || (_cache[3] = [
          _createElementVNode("div", { class: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary" }, null, -1),
          _createElementVNode("span", { class: "ml-3 text-primary-text dark:text-primary-dark-text" }, "Loading module rotations...", -1)
        ]))]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createVNode(_unref(AlertCircle), { class: "w-5 h-5 text-red-500 mr-2" }),
              _createElementVNode("span", _hoisted_11, _toDisplayString(error.value), 1)
            ]),
            _createElementVNode("button", {
              onClick: fetchRotations,
              class: "mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
            }, " Try again ")
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_12, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(monthsData.value, (month) => {
              return (_openBlock(), _createElementBlock("div", {
                key: month.number,
                class: "calendar-month relative border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer group",
                onClick: ($event: any) => (openMonthModal(month))
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["px-5 py-4 border-b border-gray-200 dark:border-gray-700 relative", month.isCurrentMonth ? 'bg-primary/10 dark:bg-primary-dark/10' : 'bg-gray-50 dark:bg-gray-700'])
                }, [
                  _createElementVNode("div", _hoisted_14, [
                    _createElementVNode("h4", _hoisted_15, _toDisplayString(month.name), 1)
                  ]),
                  _cache[4] || (_cache[4] = _createElementVNode("div", { class: "absolute inset-0 bg-gradient-to-r from-transparent to-white/5 dark:to-gray-800/5 pointer-events-none" }, null, -1))
                ], 2),
                _createElementVNode("div", _hoisted_16, [
                  (month.module)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                        _createElementVNode("div", _hoisted_18, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["flex-shrink-0 w-4 h-4 rounded-full shadow-sm border-2 border-white dark:border-gray-800", getModuleStatusColor(month.module)]),
                            "aria-hidden": "true"
                          }, null, 2),
                          _createElementVNode("div", _hoisted_19, [
                            _createElementVNode("h5", _hoisted_20, _toDisplayString(month.module.module_name), 1),
                            _createElementVNode("p", {
                              class: _normalizeClass(["text-sm mt-1 font-medium", getModuleStatusTextColor(month.module)])
                            }, _toDisplayString(getModuleStatusText(month.module)), 3)
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_21, [
                          _createElementVNode("div", _hoisted_22, [
                            _createVNode(_unref(Calendar), { class: "w-4 h-4 mr-2" }),
                            _createElementVNode("span", _hoisted_23, _toDisplayString(formatDateRange(month.module.start_date, month.module.end_date)), 1)
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_24, [
                          _createElementVNode("div", _hoisted_25, [
                            _cache[5] || (_cache[5] = _createElementVNode("span", { class: "font-medium" }, "Progress", -1)),
                            _createElementVNode("span", _hoisted_26, _toDisplayString(getMonthProgress(month.module)) + "%", 1)
                          ]),
                          _createElementVNode("div", _hoisted_27, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["h-2.5 rounded-full transition-all duration-500 ease-out shadow-sm", getProgressBarColor(month.module)]),
                              style: _normalizeStyle({ width: getMonthProgress(month.module) + '%' })
                            }, null, 6)
                          ])
                        ])
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_28, [
                        _createElementVNode("div", _hoisted_29, [
                          _createVNode(_unref(Calendar), { class: "w-8 h-8 text-gray-400 dark:text-gray-500" })
                        ]),
                        _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-sm text-secondary-text dark:text-secondary-dark-text font-medium" }, " No module assigned ", -1)),
                        _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-xs text-gray-400 dark:text-gray-500 mt-1" }, " Available for assignment ", -1))
                      ]))
                ])
              ], 8, _hoisted_13))
            }), 128))
          ])),
    _cache[8] || (_cache[8] = _createStaticVNode("<div class=\"mt-8 p-5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700\" data-v-19fcbe31><h4 class=\"text-sm font-semibold text-primary-text dark:text-primary-dark-text mb-4\" data-v-19fcbe31>Status Legend</h4><div class=\"grid grid-cols-1 sm:grid-cols-3 gap-4\" data-v-19fcbe31><div class=\"flex items-center\" data-v-19fcbe31><div class=\"w-4 h-4 rounded-full bg-green-500 mr-3 shadow-sm border-2 border-white dark:border-gray-800\" data-v-19fcbe31></div><span class=\"text-sm text-secondary-text dark:text-secondary-dark-text font-medium\" data-v-19fcbe31>Current Module</span></div><div class=\"flex items-center\" data-v-19fcbe31><div class=\"w-4 h-4 rounded-full bg-blue-500 mr-3 shadow-sm border-2 border-white dark:border-gray-800\" data-v-19fcbe31></div><span class=\"text-sm text-secondary-text dark:text-secondary-dark-text font-medium\" data-v-19fcbe31>Upcoming Module</span></div><div class=\"flex items-center\" data-v-19fcbe31><div class=\"w-4 h-4 rounded-full bg-gray-400 mr-3 shadow-sm border-2 border-white dark:border-gray-800\" data-v-19fcbe31></div><span class=\"text-sm text-secondary-text dark:text-secondary-dark-text font-medium\" data-v-19fcbe31>Past Module</span></div></div></div>", 1)),
    _createVNode(ModuleDetailsModal, {
      show: showModal.value,
      "month-data": selectedMonth.value,
      year: currentYear.value,
      onClose: closeModal
    }, null, 8, ["show", "month-data", "year"])
  ]))
}
}

})